import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DocumentService } from '../../services/document.service';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import * as pdfjsLib from 'pdfjs-dist';

// Ensure the PDF worker loads correctly from a reliable CDN
(pdfjsLib as any).GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@4.4.168/build/pdf.worker.min.mjs';

@Component({
  selector: 'app-signer',
  standalone: true,
  imports: [CommonModule, PdfViewerModule, FormsModule],
  template: `
    <div class="signer-layout" *ngIf="signerData; else loading">
      <nav class="navbar">
        <div class="logo">
          <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
          Sorrus
        </div>
        <div class="signer-badge">
          Angemeldet als <strong>{{ signerData.signer.name }}</strong>
        </div>
      </nav>

      <main class="main-content">
        <div class="document-container">
          <div class="doc-header">
            <div class="doc-header-left">
              <div class="doc-title-group">
                <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
                <div>
                  <h2>{{ signerData.document.originalName }}</h2>
                  <span class="doc-type">PDF Dokument</span>
                </div>
              </div>
            </div>
            <div class="doc-header-right">
              <div class="status-badge" [class.success]="signerData.signer.status === 'signed'">
                {{ signerData.signer.status === 'signed' ? 'Bereits unterschrieben' : 'Unterschrift erforderlich' }}
              </div>
              <button class="btn-download" (click)="downloadPdf()" title="Dokument herunterladen">
                <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                Dokument
              </button>
              <button class="btn-audit" *ngIf="signerData.document.status === 'completed' || signerData.signer.status === 'signed'" (click)="downloadAudit()" title="Audit-Zertifikat herunterladen">
                <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
                Audit-Zertifikat
              </button>
            </div>
          </div>

          <div class="pdf-wrapper">
            <div class="pdf-scroll-viewport">
              <div class="pdf-content-wrapper" *ngIf="pdfBlobUrl" [style.height.px]="pdfHeight || 800">
                <pdf-viewer 
                  [src]="pdfBlobUrl" 
                  [render-text]="false"
                  [original-size]="false"
                  [show-all]="true"
                  (after-load-complete)="onPdfLoaded($event)"
                  class="pdf-render-element"
                  style="display: block; width: 850px; height: 100%;">
                </pdf-viewer>
                
                <div class="signer-overlay" style="height: 100%;">
                  <div class="static-signature-box" 
                       *ngIf="signerData.signer.absoluteX !== undefined && signerData.signer.status !== 'signed'"
                       [style.left.px]="signerData.signer.absoluteX" 
                       [style.top.px]="signerData.signer.absoluteY"
                       [style.width.px]="signerData.signer.signatureWidth || 220"
                       [style.height.px]="signerData.signer.signatureHeight || 80">
                    <div class="sig-header">
                      <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
                      HIER UNTERSCHREIBEN
                    </div>
                    <div class="sig-body">
                      <span class="sig-name">{{ signerData.signer.name }}</span>
                      <span class="sig-email">{{ signerData.signer.email }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <aside class="action-sidebar">
          <div class="card glass">
            <h3>Ihre Unterschrift</h3>
            <p class="desc">
              Bitte prüfen Sie das Dokument sorgfältig. Mit einem Klick auf "Jetzt rechtsgültig unterschreiben" wird Ihre digitale Signatur an der vorgesehenen Stelle platziert.
            </p>

            <div class="signature-preview">
              <span class="preview-text">Vorschau:</span>
              <div class="signature-mock">
                {{ signerData.signer.name }}
              </div>
            </div>

            <button 
              class="btn-primary" 
              (click)="startVerification()" 
              [disabled]="signerData.signer.status === 'signed' || isConfirming || isVerified">
              
              <svg *ngIf="!isConfirming" viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
              <span>{{ isVerified ? 'Verifiziert' : (isConfirming ? 'Wird verarbeitet...' : 'Jetzt rechtsgültig unterschreiben') }}</span>
            </button>
            
            <div class="legal-text" *ngIf="signerData.signer.status !== 'signed' && signerData.signer.phone">
              Zuerst erhalten Sie einen Bestätigungscode per WhatsApp auf Ihr Handy, um die Unterschrift auszulösen.
            </div>
            
            <div class="legal-text" *ngIf="signerData.signer.status !== 'signed' && signerData.signer.require2FA">
              Dieses Dokument erfordert eine 2FA-Verifizierung über Ihre Authenticator App.
            </div>

            <div class="legal-text" *ngIf="signerData.signer.status !== 'signed' && !signerData.signer.phone && !signerData.signer.require2FA">
              Dieses Dokument wird direkt ohne zusätzliche SMS/WhatsApp Verifizierung unterschrieben.
            </div>
            
            <div class="success-msg" *ngIf="signerData.signer.status === 'signed'">
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
              Erfolgreich signiert am {{ signerData.signer.signedAt | date:'short' }}
            </div>
          </div>
        </aside>
      </main>

      <!-- 2FA MODAL (WhatsApp) -->
      <div class="modal-overlay" *ngIf="showVerificationModal && signerData.signer.phone">
        <div class="modal-card glass">
          <div class="modal-header">
            <svg viewBox="0 0 24 24" width="32" height="32" stroke="#25D366" stroke-width="2" fill="none"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 1 1-7.6-10.6 8.38 8.38 0 0 1 3.9.9L21 3l-1.9 4.9a8.38 8.38 0 0 1 .9 3.6Z"></path></svg>
            <h2>WhatsApp Bestätigung</h2>
          </div>
          
          <div *ngIf="verificationStep === 'request'">
            <p>Wir senden einen PIN-Code an Ihre hinterlegte Telefonnummer:</p>
            <div class="phone-display">{{ signerData.signer.phone }}</div>
            <button class="btn-primary" (click)="sendCode()">
              Code per WhatsApp anfordern
            </button>
          </div>

          <div *ngIf="verificationStep === 'verify'">
            <p>Bitte geben Sie den 6-stelligen Code ein, den wir Ihnen gesendet haben:</p>
            <input [(ngModel)]="verificationCode" placeholder="000000" class="pin-input" maxlength="6">
            <button class="btn-primary" (click)="verifyCode()" [disabled]="verificationCode.length < 6 || isVerifying">
              {{ isVerifying ? 'Wird geprüft...' : 'Code bestätigen' }}
            </button>
            <button class="btn-link" (click)="verificationStep = 'request'">Code erneut senden</button>
          </div>

          <button class="btn-close" (click)="showVerificationModal = false">Abbrechen</button>
        </div>
      </div>

      <!-- 2FA MODAL (TOTP) -->
      <div class="modal-overlay" *ngIf="showVerificationModal && signerData.signer.require2FA">
        <div class="modal-card glass">
          <div class="modal-header">
            <svg viewBox="0 0 24 24" width="32" height="32" stroke="#38bdf8" stroke-width="2" fill="none"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
            <h2>2FA Verifizierung</h2>
          </div>
          
          <div *ngIf="verificationStep === 'request'">
            <p>Bitte richten Sie 2FA ein, falls noch nicht geschehen:</p>
            <img *ngIf="totpQr" [src]="totpQr" class="qr-img-large">
            <p style="font-size: 0.8rem; color: #64748b; margin-top: 1rem;">Scannen Sie den Code mit Ihrer Authenticator App.</p>
            <button class="btn-primary" style="margin-top: 1rem;" (click)="verificationStep = 'verify'">
              Ich habe den Code gescannt
            </button>
          </div>

          <div *ngIf="verificationStep === 'verify'">
            <p>Bitte geben Sie den aktuellen 6-stelligen Code aus Ihrer App ein:</p>
            <input [(ngModel)]="verificationCode" placeholder="000000" class="pin-input" maxlength="6">
            <button class="btn-primary" (click)="verifyTotp()" [disabled]="verificationCode.length < 6 || isVerifying">
              {{ isVerifying ? 'Wird geprüft...' : 'Code bestätigen' }}
            </button>
            <button class="btn-link" (click)="verificationStep = 'request'">QR-Code erneut anzeigen</button>
          </div>

          <button class="btn-close" (click)="showVerificationModal = false">Abbrechen</button>
        </div>
      </div>
    </div>
    
    <ng-template #loading>
      <div class="loading-screen">
        <div class="spinner"></div>
        <p>Dokument wird geladen...</p>
      </div>
    </ng-template>
  `,
  styles: [`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Kalam:wght@400;700&display=swap');

    :host {
      font-family: 'Inter', sans-serif;
      display: block;
      min-height: 100vh;
      background-color: #f1f5f9;
      color: #0f172a;
    }

    .signer-layout {
      display: flex;
      flex-direction: column;
      height: 100vh;
    }

    .navbar {
      background: #0f172a;
      padding: 1rem 2rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: white;
    }

    .logo {
      font-weight: 700;
      font-size: 1.25rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #38bdf8;
    }

    .signer-badge {
      background: rgba(255,255,255,0.1);
      padding: 0.5rem 1rem;
      border-radius: 999px;
      font-size: 0.875rem;
    }

    .main-content {
      display: flex;
      flex: 1;
      overflow: hidden;
      padding: 2rem;
      gap: 2rem;
      max-width: 1400px;
      margin: 0 auto;
      width: 100%;
      box-sizing: border-box;
    }

    .document-container {
      flex: 1;
      display: flex;
      flex-direction: column;
      background: white;
      border-radius: 1rem;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      overflow: hidden;
    }

    .doc-header {
      padding: 1.25rem 2rem;
      border-bottom: 1px solid #e2e8f0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;
    }

    .doc-header-left { display: flex; align-items: center; min-width: 0; flex: 1; }
    .doc-header-right { display: flex; align-items: center; gap: 0.75rem; flex-shrink: 0; }

    .doc-title-group {
      display: flex; align-items: center; gap: 0.75rem;
      color: #64748b; min-width: 0;
    }
    .doc-title-group svg { flex-shrink: 0; color: #0ea5e9; }
    .doc-title-group h2 { margin: 0; font-size: 1.1rem; font-weight: 600; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 340px; }
    .doc-type { font-size: 0.72rem; color: #94a3b8; font-weight: 500; display: block; margin-top: 1px; }

    .btn-download {
      display: flex; align-items: center; gap: 0.4rem;
      padding: 0.45rem 0.9rem;
      background: #f1f5f9;
      border: 1px solid #e2e8f0;
      border-radius: 0.5rem;
      color: #374151;
      font-size: 0.8rem;
      font-weight: 600;
      font-family: 'Inter', sans-serif;
      cursor: pointer;
      transition: all 0.2s;
      white-space: nowrap;
    }
    .btn-download:hover { background: #0ea5e9; border-color: #0ea5e9; color: white; }
    .btn-download svg { transition: color 0.2s; }

    .btn-audit {
      display: flex; align-items: center; gap: 0.4rem;
      padding: 0.45rem 0.9rem;
      background: #0f172a;
      border: 1px solid #1e293b;
      border-radius: 0.5rem;
      color: #38bdf8;
      font-size: 0.8rem;
      font-weight: 600;
      font-family: 'Inter', sans-serif;
      cursor: pointer;
      transition: all 0.2s;
      white-space: nowrap;
    }
    .btn-audit:hover { background: #1e293b; color: white; box-shadow: 0 4px 12px rgba(56, 189, 248, 0.2); }

    .status-badge {
      padding: 0.25rem 0.75rem;
      background: #fef3c7;
      color: #b45309;
      border-radius: 999px;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
    }
    .status-badge.success {
      background: #d1fae5;
      color: #047857;
    }

    .pdf-wrapper {
      flex: 1;
      background: #cbd5e1;
      position: relative;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .pdf-scroll-viewport {
      flex: 1; overflow: auto; padding: 2rem;
      display: flex; justify-content: center;
      background: #475569;
    }

    .pdf-content-wrapper {
      position: relative;
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3);
      background: white;
      height: fit-content;
    }

    .pdf-render-element {
      display: block;
      min-height: 800px;
    }

    .signer-overlay {
      position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 10; pointer-events: none;
    }

    .static-signature-box {
      position: absolute; 
      background: #f8fafc; 
      border: 2px solid #0284c7;
      border-radius: 4px; 
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      pointer-events: none;
      overflow: hidden; animation: pulse 2s infinite;
      container-type: size;
    }
    
    .static-signature-box.signed {
      border-color: #10b981; animation: none; opacity: 0.95;
    }
    .static-signature-box.signed .sig-header { background: #10b981; }

    @keyframes pulse {
      0% { box-shadow: 0 0 0 0 rgba(2, 132, 199, 0.4); }
      70% { box-shadow: 0 0 0 15px rgba(2, 132, 199, 0); }
      100% { box-shadow: 0 0 0 0 rgba(2, 132, 199, 0); }
    }

    .sig-header { 
      background: #0284c7; color: white; font-size: 0.65rem; font-weight: 600; 
      text-transform: uppercase; padding: 4px 8px; letter-spacing: 0.05em; 
      display: flex; align-items: center; gap: 6px; height: 24px; box-sizing: border-box;
    }
    .sig-body { 
      width: 100%; height: calc(100% - 24px); display: flex; flex-direction: column; 
      align-items: center; justify-content: center; text-align: center;
      padding: 0 8px; box-sizing: border-box;
    }
    .sig-name { 
      font-family: 'Kalam', cursive; color: #0f172a; font-weight: 700; 
      font-size: min(12cqw, 35cqh); white-space: nowrap; overflow: hidden; 
      text-overflow: ellipsis; max-width: 100%; line-height: 1.2;
    }
    .sig-email {
      font-family: 'Inter', sans-serif; color: #64748b;
      font-size: min(7cqw, 20cqh); white-space: nowrap; overflow: hidden;
      text-overflow: ellipsis; max-width: 100%; line-height: 1.2;
    }

    .action-sidebar {
      width: 350px;
      display: flex;
      flex-direction: column;
    }

    .card {
      background: white;
      border-radius: 1rem;
      padding: 2rem;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }

    .card h3 { margin: 0 0 1rem; color: #0f172a; }
    .desc { color: #64748b; font-size: 0.9rem; line-height: 1.6; margin-bottom: 2rem; }

    .signature-preview {
      margin-bottom: 2rem;
      padding: 1rem;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 0.5rem;
    }

    .preview-text { font-size: 0.75rem; color: #94a3b8; text-transform: uppercase; font-weight: 600; margin-bottom: 0.5rem; display: block; }
    
    .signature-mock {
      font-family: 'Kalam', cursive;
      font-size: 2.2rem;
      color: #1e3a8a;
      text-align: center;
      padding: 1rem 0;
    }

    .btn-primary {
      width: 100%; padding: 1rem; border-radius: 0.5rem; border: none;
      background: linear-gradient(135deg, #0ea5e9, #4f46e5); color: white;
      font-weight: 600; font-size: 1rem; display: flex; align-items: center; justify-content: center; gap: 0.5rem;
      cursor: pointer; transition: all 0.2s ease;
      box-shadow: 0 10px 15px -3px rgba(14, 165, 233, 0.3);
    }
    .btn-primary:hover:not([disabled]) { transform: translateY(-2px); box-shadow: 0 15px 20px -3px rgba(14, 165, 233, 0.4); }
    .btn-primary[disabled] { opacity: 0.7; cursor: not-allowed; background: #94a3b8; box-shadow: none; }

    .legal-text {
      margin-top: 1rem; font-size: 0.75rem; color: #94a3b8; text-align: center; line-height: 1.5;
    }

    .success-msg {
      margin-top: 1.5rem; padding: 1rem; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 0.5rem;
      color: #16a34a; font-weight: 500; display: flex; align-items: center; gap: 0.5rem; font-size: 0.875rem;
    }

    .loading-screen {
      height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #0f172a; color: white;
    }
    .spinner {
      width: 40px; height: 40px; border: 4px solid rgba(255,255,255,0.1); border-top-color: #38bdf8; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 1rem;
    }
    @keyframes spin { 100% { transform: rotate(360deg); } }

    .modal-overlay {
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(15, 23, 42, 0.9); backdrop-filter: blur(8px);
      z-index: 1000; display: flex; align-items: center; justify-content: center;
    }
    .modal-card {
      width: 400px; background: white; border-radius: 1.5rem; padding: 2.5rem; text-align: center;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
    }
    .modal-header { margin-bottom: 1.5rem; display: flex; flex-direction: column; align-items: center; gap: 1rem; }
    .modal-header h2 { margin: 0; color: #1e293b; }
    
    .phone-display {
      font-size: 1.25rem; font-weight: 700; color: #0284c7; margin: 1.5rem 0;
      padding: 0.75rem; background: #f0f9ff; border-radius: 0.75rem; border: 1px dashed #bae6fd;
    }

    .pin-input {
      width: 100%; font-size: 2rem; letter-spacing: 0.5rem; text-align: center;
      padding: 1rem; border-radius: 0.75rem; border: 2px solid #e2e8f0; margin-bottom: 1.5rem;
      font-weight: 700; color: #1e293b;
    }
    .pin-input:focus { border-color: #0ea5e9; outline: none; }

    .btn-link {
      background: none; border: none; color: #0ea5e9; font-weight: 600; cursor: pointer; margin-top: 1rem;
    }
    .btn-close {
      background: none; border: none; color: #94a3b8; font-size: 0.9rem; cursor: pointer; margin-top: 2rem;
    }

    .qr-img-large {
      width: 200px; height: 200px; margin: 0 auto; display: block;
      padding: 10px; background: white; border-radius: 1rem; box-shadow: 0 10px 25px rgba(0,0,0,0.1);
    }
  `]
})
export class SignerComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private documentService = inject(DocumentService);

  token: string = '';
  signerData: any = null;
  isConfirming = false;
  pdfHeight: number = 0;
  pdfBlobUrl: string = '';

  showVerificationModal = false;
  verificationStep: 'request' | 'verify' = 'request';
  verificationCode = '';
  isVerifying = false;
  isVerified = false;
  totpQr: string = '';

  ngOnInit() {
    this.token = this.route.snapshot.paramMap.get('token') || '';
    if (this.token) {
      this.documentService.getSignerByToken(this.token, this.getBrowserData()).subscribe({
        next: (data) => {
          this.signerData = data;
          // Fetch PDF as Blob
          this.documentService.getPdfBlob(data.document.id).subscribe(blob => {
            this.pdfBlobUrl = URL.createObjectURL(blob);
          });
        },
        error: () => alert('Ungültiger oder abgelaufener Link!')
      });
    }
  }

  onPdfLoaded(pdfData: any) {
    setTimeout(() => {
      const pdfViewerEl = document.querySelector('.pdfViewer');
      if (pdfViewerEl) {
        this.pdfHeight = pdfViewerEl.scrollHeight;
      }
      
      const pages = document.querySelectorAll('.page');
      if (this.signerData && this.signerData.signer.signatureX !== null && pages.length > 0) {
        const pageIndex = this.signerData.signer.signaturePage || 0;
        const pageEl = pages[pageIndex < pages.length ? pageIndex : 0] as HTMLElement;
        
        if (pageEl) {
          this.signerData.signer.absoluteX = this.signerData.signer.signatureX + pageEl.offsetLeft;
          this.signerData.signer.absoluteY = this.signerData.signer.signatureY + pageEl.offsetTop;
        }
      }
    }, 500);
  }

  startVerification() {
    if (this.isVerified) {
      this.confirm();
      return;
    }

    if (this.signerData.signer.require2FA) {
      this.showVerificationModal = true;
      this.verificationStep = 'request';
      if (!this.totpQr) {
        this.documentService.getSigner2faQr(this.signerData.signer.id).subscribe(res => {
          this.totpQr = res.qrCode;
        });
      }
      return;
    }

    if (this.signerData.signer.phone) {
      this.showVerificationModal = true;
      this.verificationStep = 'request';
      return;
    }

    this.confirm();
  }

  sendCode() {
    this.documentService.sendWhatsAppCode(this.signerData.signer.id).subscribe(() => {
      this.verificationStep = 'verify';
    });
  }

  getBrowserData() {
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: (navigator as any).platform,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
    };
  }

  verifyCode() {
    this.isVerifying = true;
    this.documentService.verifyWhatsAppCode(
      this.signerData.signer.id, 
      this.verificationCode,
      this.getBrowserData()
    ).subscribe({
      next: () => {
        this.isVerified = true;
        this.isVerifying = false;
        this.showVerificationModal = false;
        this.confirm();
      },
      error: () => {
        alert('Falscher Code. Bitte versuchen Sie es erneut.');
        this.isVerifying = false;
        this.verificationCode = '';
      }
    });
  }

  confirm() {
    this.isConfirming = true;
    this.documentService.confirmSignature(this.token, this.getBrowserData()).subscribe({
      next: (res) => {
        this.signerData.signer.status = 'signed';
        this.signerData.signer.signedAt = new Date();
        this.isConfirming = false;
        
        // Reload PDF to show the burnt-in signature
        this.documentService.getPdfBlob(this.signerData.document.id).subscribe(blob => {
          this.pdfBlobUrl = URL.createObjectURL(blob);
        });
      },
      error: (err) => {
        alert('Fehler beim Signieren: ' + (err.error?.message || 'Unbekannter Fehler'));
        this.isConfirming = false;
      }
    });
  }

  verifyTotp() {
    this.isVerifying = true;
    this.documentService.verifySigner2fa(
      this.signerData.signer.id,
      this.verificationCode,
      this.getBrowserData()
    ).subscribe({
      next: () => {
        this.isVerified = true;
        this.isVerifying = false;
        this.showVerificationModal = false;
        this.confirm();
      },
      error: () => {
        alert('Falscher Code. Bitte versuchen Sie es erneut.');
        this.isVerifying = false;
        this.verificationCode = '';
      }
    });
  }

  downloadPdf() {
    const docId = this.signerData?.document?.id;
    const filename = this.signerData?.document?.originalName || 'dokument.pdf';
    if (!docId) return;

    this.documentService.getPdfBlob(docId).subscribe(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename.endsWith('.pdf') ? filename : filename + '.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
  }

  downloadAudit() {
    const docId = this.signerData?.document?.id;
    if (!docId) return;

    this.documentService.getAuditBlob(docId).subscribe(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Audit-Zertifikat-${this.signerData.document.originalName}`;
      if (!a.download.endsWith('.pdf')) a.download += '.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
  }
}
